import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { GALLERY, IMAGES } from "@/lib/data";

export default function Gallery() {
  const tags = useMemo(() => ["All", ...Array.from(new Set(GALLERY.map((g) => g.tag)))], []);
  const [tag, setTag] = useState("All");
  const [active, setActive] = useState(null);

  const items = tag === "All" ? GALLERY : GALLERY.filter((g) => g.tag === tag);

  return (
    <main className="bg-beige" data-testid="gallery-page">
      <section className="relative pt-40 pb-20 bg-forest text-beige overflow-hidden grain">
        <img src={IMAGES.herbs} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest to-forest/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="overline text-gold mb-4">A Visual Journey</p>
          <h1 className="font-serif text-6xl md:text-7xl leading-none">From our fields to<br /><span className="italic text-gold">your gallery.</span></h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-wrap gap-2 mb-10">
          {tags.map((t) => (
            <button key={t} data-testid={`gallery-filter-${t.toLowerCase()}`} onClick={() => setTag(t)}
              className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${tag === t ? "bg-forest text-beige border-forest" : "border-forest/30 text-forest hover:border-forest"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" data-testid="gallery-grid">
          {items.map((g, i) => (
            <motion.button
              key={g.src}
              layout
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              onClick={() => setActive(g)} data-testid={`gallery-item-${i}`}
              className="group relative mb-5 w-full overflow-hidden rounded-2xl block break-inside-avoid">
              <img src={g.src} alt={g.alt} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 p-5 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                <span className="overline text-gold text-[10px]">{g.tag}</span>
                <p className="text-beige font-serif text-lg leading-tight">{g.alt}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)} data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] bg-forest/95 backdrop-blur-sm flex items-center justify-center p-6">
            <button data-testid="gallery-lightbox-close" className="absolute top-6 right-6 w-11 h-11 rounded-full border border-beige/40 text-beige flex items-center justify-center hover:bg-gold hover:text-forest hover:border-gold transition-colors">
              <X className="w-5 h-5" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              src={active.src} alt={active.alt} onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full object-contain rounded-xl" />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
